import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, subMonths } from 'date-fns'
import { Clock, DollarSign, Users, Building2, Download, Globe } from 'lucide-react'
import { timeTrackingApi, clientsApi, usersApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import { TimeReportFilters, UserListItem } from '@/types'
import { useAgencyTheme } from '@/contexts/AgencyThemeContext'
import { getTimezoneAbbreviation } from '@/utils/timezone'

type DatePreset = 'this_week' | 'last_week' | 'this_month' | 'last_month' | 'custom'

export default function TimeReportsPage() {
  const { t } = useTranslation()
  const { timezone } = useAgencyTheme()
  const [datePreset, setDatePreset] = useState<DatePreset>('this_month')
  const [customStartDate, setCustomStartDate] = useState('')
  const [customEndDate, setCustomEndDate] = useState('')
  const [selectedUserId, setSelectedUserId] = useState<string>('')
  const [selectedClientId, setSelectedClientId] = useState<string>('')

  // Calculate date range based on preset
  const getDateRange = () => {
    const now = new Date()
    switch (datePreset) {
      case 'this_week':
        return {
          start: format(startOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
          end: format(endOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
        }
      case 'last_week':
        const lastWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
        return {
          start: format(startOfWeek(lastWeek, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
          end: format(endOfWeek(lastWeek, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
        }
      case 'this_month':
        return {
          start: format(startOfMonth(now), 'yyyy-MM-dd'),
          end: format(endOfMonth(now), 'yyyy-MM-dd'),
        }
      case 'last_month':
        const lastMonth = subMonths(now, 1)
        return {
          start: format(startOfMonth(lastMonth), 'yyyy-MM-dd'),
          end: format(endOfMonth(lastMonth), 'yyyy-MM-dd'),
        }
      case 'custom':
        return {
          start: customStartDate || format(startOfMonth(now), 'yyyy-MM-dd'),
          end: customEndDate || format(endOfMonth(now), 'yyyy-MM-dd'),
        }
    }
  }

  const { start: startDate, end: endDate } = getDateRange()

  // Build filters
  const filters: TimeReportFilters = {
    start_date: startDate,
    end_date: endDate,
    user_id: selectedUserId || undefined,
    client_id: selectedClientId || undefined,
  }

  // Fetch report summary
  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['timeReports', 'summary', filters],
    queryFn: () => timeTrackingApi.getReportSummary(filters),
    enabled: !!startDate && !!endDate,
  })

  // Fetch clients for filter
  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: clientsApi.getAllLegacy,
  })

  // Fetch users for filter
  const { data: users = [] } = useQuery<UserListItem[]>({
    queryKey: ['users'],
    queryFn: () => usersApi.getAll(),
  })

  // Calculate max hours for progress bars
  const maxClientHours = summary?.by_client?.reduce((max, c) => Math.max(max, c.total_hours), 0) || 1
  const maxUserHours = summary?.by_user?.reduce((max, u) => Math.max(max, u.total_hours), 0) || 1

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold">{t('timeTracking.reports')}</h1>
          <div className="flex items-center gap-2">
            <p className="text-muted-foreground">{t('timeTracking.reportsSubtitle')}</p>
            <span className="text-xs text-muted-foreground flex items-center gap-1 bg-muted px-2 py-0.5 rounded">
              <Globe className="h-3 w-3" />
              {getTimezoneAbbreviation(timezone)}
            </span>
          </div>
        </div>
        <Button variant="outline">
          <Download className="h-4 w-4 mr-2" />
          {t('common.export')}
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Date Preset */}
            <div className="grid gap-2">
              <Label>{t('timeTracking.dateRange')}</Label>
              <Select value={datePreset} onValueChange={(v) => setDatePreset(v as DatePreset)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="this_week">{t('timeTracking.thisWeek')}</SelectItem>
                  <SelectItem value="last_week">{t('timeTracking.lastWeek')}</SelectItem>
                  <SelectItem value="this_month">{t('timeTracking.thisMonth')}</SelectItem>
                  <SelectItem value="last_month">{t('timeTracking.lastMonth')}</SelectItem>
                  <SelectItem value="custom">{t('timeTracking.custom')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Custom Date Range */}
            {datePreset === 'custom' && (
              <>
                <div className="grid gap-2">
                  <Label>{t('timeTracking.startDate')}</Label>
                  <Input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>{t('timeTracking.endDate')}</Label>
                  <Input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                  />
                </div>
              </>
            )}

            {/* User Filter */}
            <div className="grid gap-2">
              <Label>{t('timeTracking.user')}</Label>
              <Select value={selectedUserId || 'all'} onValueChange={(val) => setSelectedUserId(val === 'all' ? '' : val)}>
                <SelectTrigger>
                  <SelectValue placeholder={t('timeTracking.allUsers')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('timeTracking.allUsers')}</SelectItem>
                  {users.map((user) => (
                    <SelectItem key={user.user_id} value={user.user_id}>
                      {user.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Client Filter */}
            <div className="grid gap-2">
              <Label>{t('timeTracking.client')}</Label>
              <Select value={selectedClientId || 'all'} onValueChange={(val) => setSelectedClientId(val === 'all' ? '' : val)}>
                <SelectTrigger>
                  <SelectValue placeholder={t('timeTracking.allClients')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('timeTracking.allClients')}</SelectItem>
                  {clients.map((client) => (
                    <SelectItem key={client.client_id} value={client.client_id}>
                      {client.client_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Clock className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('timeTracking.totalHours')}</p>
                {summaryLoading ? (
                  <Skeleton className="h-8 w-20" />
                ) : (
                  <p className="text-2xl font-bold">{summary?.total_hours?.toFixed(1) || 0}h</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <DollarSign className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('timeTracking.billableHours')}</p>
                {summaryLoading ? (
                  <Skeleton className="h-8 w-20" />
                ) : (
                  <p className="text-2xl font-bold">{summary?.billable_hours?.toFixed(1) || 0}h</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <Building2 className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('timeTracking.clientsTracked')}</p>
                {summaryLoading ? (
                  <Skeleton className="h-8 w-20" />
                ) : (
                  <p className="text-2xl font-bold">{summary?.by_client?.length || 0}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                <Users className="h-6 w-6 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('timeTracking.teamMembers')}</p>
                {summaryLoading ? (
                  <Skeleton className="h-8 w-20" />
                ) : (
                  <p className="text-2xl font-bold">{summary?.by_user?.length || 0}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Client Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('timeTracking.byClient')}</CardTitle>
            <CardDescription>{t('timeTracking.byClientDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            {summaryLoading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : summary?.by_client?.length ? (
              <div className="space-y-4">
                {summary.by_client.map((client) => (
                  <div key={client.client_id || 'no-client'} className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="font-medium">
                        {client.client_name || t('timeTracking.noClient')}
                      </span>
                      <span className="text-muted-foreground">
                        {client.total_hours.toFixed(1)}h
                      </span>
                    </div>
                    <Progress
                      value={(client.total_hours / maxClientHours) * 100}
                      className="h-2"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">
                {t('timeTracking.noData')}
              </p>
            )}
          </CardContent>
        </Card>

        {/* User Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>{t('timeTracking.byUser')}</CardTitle>
            <CardDescription>{t('timeTracking.byUserDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            {summaryLoading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : summary?.by_user?.length ? (
              <div className="space-y-4">
                {summary.by_user.map((user) => (
                  <div key={user.user_id} className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="font-medium">{user.user_name}</span>
                      <span className="text-muted-foreground">
                        {user.total_hours.toFixed(1)}h
                      </span>
                    </div>
                    <Progress
                      value={(user.total_hours / maxUserHours) * 100}
                      className="h-2"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">
                {t('timeTracking.noData')}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
